import { useRef } from 'react'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { type Theme, alpha } from '@mui/material/styles'

import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import type { HoofdleidingProject } from '../../project/types/HoofdleidingProject'
import { PLANNING_LIMIT_OPTIONS } from '../services/searchProjectenPlanning'
import {
  getAllCols,
  getCurrentWeekIndex,
  getDenseRangeDict,
  getProjectCountPerWeek,
  getProjectDisplayRange,
  indexGeblokkeerdeWeken,
} from '../util/planning-util'

import { BalkenplanningChip } from './BalkenplanningChip'
import { BalkenplanningProjectRange } from './BalkenplanningProjectRange'

export { Balkenplanning }

const APP_BAR_HEIGHT = 64
const LABEL_COL_WIDTH = 200
const WEEK_COL_WIDTH = 60
const CELL_PADDING_X = 2
const CURRENT_WEEK_OPACITY = 0.12
const CURRENT_WEEK_STROKE_WIDTH = 2

const labelCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  overflow: 'hidden',
  bgcolor: 'background.paper',
} as const

const currentWeekSx = {
  bgcolor: (theme: Theme) =>
    alpha(theme.palette.primary.main, CURRENT_WEEK_OPACITY),
  boxShadow: (theme: Theme) =>
    `inset ${CURRENT_WEEK_STROKE_WIDTH}px 0 ${theme.palette.primary.main}, inset -${CURRENT_WEEK_STROKE_WIDTH}px 0 ${theme.palette.primary.main}`,
} as const

interface BalkenplanningProps {
  projects: HoofdleidingProject[]
  geblokkeerdeWeken: GeblokkeerdeWeek[]
  total: number
  limit: number
  onChangeLimit: (limit: number) => void
}

const NoPageActions = () => <Box sx={{ width: 24 }} />

const Balkenplanning = ({
  projects,
  geblokkeerdeWeken,
  total,
  limit,
  onChangeLimit,
}: BalkenplanningProps) => {
  const rangeDict = getDenseRangeDict(projects)
  const allCols = getAllCols(rangeDict)
  const counts = getProjectCountPerWeek(projects)
  const geblokkeerdeWekenIndex = indexGeblokkeerdeWeken(geblokkeerdeWeken)
  const currentWeekIndex = getCurrentWeekIndex(allCols)

  const headerRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  const weekCount = allCols.length

  const tableSx = {
    tableLayout: 'fixed',
    width: LABEL_COL_WIDTH + weekCount * WEEK_COL_WIDTH,
  } as const

  const colGroup = (
    <Box component="colgroup">
      <Box component="col" sx={{ width: LABEL_COL_WIDTH }} />
      {Array.from({ length: weekCount }).map((_, index) => (
        <Box key={index} component="col" sx={{ width: WEEK_COL_WIDTH }} />
      ))}
    </Box>
  )

  const syncHeaderScroll = () => {
    if (headerRef.current && bodyRef.current) {
      headerRef.current.scrollLeft = bodyRef.current.scrollLeft
    }
  }

  return (
    <Paper>
      <TablePagination
        component="div"
        count={total}
        page={0}
        rowsPerPage={limit}
        rowsPerPageOptions={PLANNING_LIMIT_OPTIONS}
        /* v8 ignore next - required by MUI, niet nodig voor balkenplanning*/
        onPageChange={() => {}}
        onRowsPerPageChange={({ target }) =>
          onChangeLimit(Number(target.value))
        }
        labelRowsPerPage="Resultaten op scherm"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} van ${count === -1 ? `meer dan ${to}` : count} zichtbaar`
        }
        ActionsComponent={NoPageActions}
      />

      <Divider />

      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: APP_BAR_HEIGHT,
          zIndex: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Table size="small" sx={tableSx}>
          {colGroup}

          <TableHead>
            <TableRow>
              <TableCell align="right" sx={labelCellSx}>
                Jaar
              </TableCell>

              {Object.entries(rangeDict).map(([year, weeks]) => (
                <TableCell key={year} colSpan={weeks.length}>
                  <Box
                    sx={{
                      position: 'sticky',
                      left: (theme) =>
                        `calc(${LABEL_COL_WIDTH}px + ${theme.spacing(CELL_PADDING_X)})`,
                      display: 'inline-block',
                    }}
                  >
                    {year}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell align="right" sx={labelCellSx}>
                Week
              </TableCell>

              {allCols.map(([year, week], index) => (
                <TableCell
                  key={`${year}-${week}`}
                  aria-current={index === currentWeekIndex ? 'date' : undefined}
                  sx={index === currentWeekIndex ? currentWeekSx : undefined}
                >
                  {week}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell align="right" sx={labelCellSx}>
                Projecten in week
              </TableCell>

              {allCols.map(([year, week], index) => (
                <TableCell
                  key={`${year}-${week}`}
                  sx={index === currentWeekIndex ? currentWeekSx : undefined}
                >
                  {counts[`${year}-${week}`] || 0}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
      </Box>

      <TableContainer
        ref={bodyRef}
        onScroll={syncHeaderScroll}
        sx={{ position: 'relative' }}
      >
        {currentWeekIndex !== -1 && (
          <Box
            data-testid="current-week-stroke"
            sx={{
              ...currentWeekSx,
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: LABEL_COL_WIDTH + currentWeekIndex * WEEK_COL_WIDTH,
              width: WEEK_COL_WIDTH,
              pointerEvents: 'none',
            }}
          />
        )}

        <Table size="small" sx={tableSx}>
          {colGroup}

          <TableBody>
            {projects.map((project) => {
              const { emptyColsBefore, activeCols, emptyColsAfter } =
                getProjectDisplayRange(project, allCols)

              return (
                <TableRow key={project.aanvraagID}>
                  <TableCell sx={{ ...labelCellSx, background: 'transparent' }}>
                    <BalkenplanningChip project={project} />
                  </TableCell>

                  {emptyColsBefore > 0 && (
                    <TableCell colSpan={emptyColsBefore} />
                  )}

                  <BalkenplanningProjectRange
                    project={project}
                    activeCols={activeCols}
                    geblokkeerdeWeken={geblokkeerdeWekenIndex}
                  />

                  {emptyColsAfter > 0 && <TableCell colSpan={emptyColsAfter} />}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
